import {
	FAVORITE_PREFIX,
	ItemFavoriteStore,
	ILineItem,
	IRecentItem,
} from '../store';
import {ItemService} from '../services';

const RE_NATURAL_NUMBER = /^\d+$/;

export class DisplayUtils {
	/**
	 * preserves desired fractional format
	 *
	 * Some fonts, including our Clarika font, support auto-formatting fractions, which Philipp
	 * does NOT want. `font-variant-numeric: normal` did not disable it, nor did inserting SPANs
	 * at/around different parts of the string. We need to insert non-fraction characters,
	 * such as a zero-width space, to trick the auto-formatting into leaving it alone.
	 */
	public static displayFlatFraction<T extends string | unknown>(value: T): T extends string ? string : unknown {
		if(typeof value === 'string') {
			const fractional = value.split('/');
			if(fractional.length === 2 &&
				RE_NATURAL_NUMBER.test(fractional[0]) &&
				RE_NATURAL_NUMBER.test(fractional[1])
			) {
				return `${fractional[0]}&ZeroWidthSpace;/&ZeroWidthSpace;${fractional[1]}` as T extends string ? string : unknown;
			} else {
				// strip any HTML
				// https://stackoverflow.com/a/47140708/356016
				const tempDoc = new DOMParser().parseFromString(value, 'text/html');
				return (tempDoc.body.textContent ?? '') as T extends string ? string : unknown;
			}
		}

		// type !== string
		return value as T extends string ? string : unknown;
	}

	public static getLineItemDisplayName(
		lineItem: ILineItem | null,
		options?: {
			includeQuantity?: boolean;
		},
	): string {
		const {includeQuantity} = {
			includeQuantity: false,
			...options
		};

		if(lineItem === null) {
			return '';
		}

		let displayName = lineItem.name ?? '';

		if(ItemService.isEngagementSource(lineItem.engagementSource)) {
			if(ItemService.engagementSourceIsFavorite(lineItem.engagementSource)) {
				if(ItemFavoriteStore.favorite(lineItem.engagementSource.id)) {
					displayName = FAVORITE_PREFIX + lineItem.engagementSource.name;
				}
			} else {
				displayName = lineItem.engagementSource.name;
			}
		}

		if(includeQuantity && lineItem?.quantity > 1) {
			// TODO?: convert "x" to &times;
			displayName = `${lineItem.quantity}x ${displayName}`;
		}

		return displayName;
	}

	public static getRecentItemDisplayName(recentItem: IRecentItem | null): string {
		if(recentItem === null) {
			return '';
		}

		const favorite = ItemFavoriteStore.favoriteFromEngagementSource(recentItem.engagementSource);

		if(favorite !== null && favorite?.name) {
			return FAVORITE_PREFIX + favorite.name;
		}

		return recentItem.name ?? '';
	}
}
